import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../components/base/BasicDialog";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { useTransfersInvestmentBillsByAgreementId } from "../../services/bankingService";
import { useTransferTableFieldsConfig } from "./ReservationTransfersForAgreementDialogFieldsConfig";
import { Grid, Button, Typography } from "@mui/material";
import EditableTable from "../../components/table/EditableTable";
import TableService from "../../services/tableService";
import { StyledTableRow, StyledTableCell } from "../../components/table/BaseTable/BaseTable";
import { formatCurrency } from "../../components/common/PriceDisplay";

import DateService from "../../services/dateService";

import { 
    TRANSFER_STATUS_KIND_PENDING, 
    TRANSFER_STATUS_KIND_PARTIAL_PAYMENT, 
    TRANSFER_STATUS_KIND_PAID, 
    TRANSFER_STATUS_KIND_EXCESS_PAYMENT 
} from "../../schemas/banking";

import { LoadingButton } from "@mui/lab";

import { useToasts } from "../../hooks/useToasts";
import toast from "react-hot-toast";
import { useMassCreateInvestmentTransferMutation, useRetrunInvestmentTransferForAgreementMutation } from "../../services/bankingService";

import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";

export function ReservationTransfersForAgreementDialog(props) {
    const { t } = useTranslation();

    const [transferDataLocal, setTransferDataLocal] = useState();

    const { data: transfersData } = useTransfersInvestmentBillsByAgreementId(props.agreementId);
    const TABLE_CONFIG = useTransferTableFieldsConfig({isForInvoicingPage: props.isForInvoicingPage})

    useEffect(() => {
        if (transfersData?.results && !transferDataLocal) {
            let tempTransferData = [...(transfersData?.results || [])]
            for (let tempTransfer of  tempTransferData) {
                if (!tempTransfer.transfer_date){
                    tempTransfer.transfer_date = DateService.getIsoDateFromDatetime(new Date())
                }
            }
            setTransferDataLocal(tempTransferData);
        }
    }, [transfersData]);

    const addEmptyTransfer = () => {
        if (!transferDataLocal) {
            return
        }
        let tempTransfer = {...transferDataLocal[0]}
        if (tempTransfer.id) {
            delete tempTransfer.id
        }
        
        tempTransfer.transfer_amount = 0
        tempTransfer.transfer_docs = null
        tempTransfer.transfer_date = DateService.getIsoDateFromDatetime(new Date())

        const transferStatus = TRANSFER_STATUS_KIND_PENDING
        tempTransfer.transfer_status_id = transferStatus.id
        tempTransfer.transfer_status_kind = transferStatus.transfer_status_kind
        tempTransfer.transfer_status_name = transferStatus.transfer_status_name

        setTransferDataLocal(prev=>([tempTransfer, ...prev]))
    }

    const handleDelete = (index) => {
        setTransferDataLocal(prev=> {
            if (prev.length === 1) {
                return prev
            }
            let tempDataLocal = [...prev]
            tempDataLocal = updateRemainingTotalPriceAndTransferStatus(tempDataLocal.filter((data, idx)=> idx !== index))
            return tempDataLocal
        })
    }

    const handleAddDocs = (idx, docsId, docsNote) => {
        setTransferDataLocal((prev) => {
            let tempDataLocal = [...prev];
            let transfer = { ...tempDataLocal[idx] };
            transfer.transfer_docs = {docs_id: docsId, docs_note: docsNote}
            tempDataLocal[idx] = { ...transfer };
            return tempDataLocal;
        });
    }

    const handleDeleteDocs = (idx) => {
        setTransferDataLocal((prev) => {
            let tempDataLocal = [...prev];
            let transfer = { ...tempDataLocal[idx] };
            transfer.transfer_docs = null
            tempDataLocal[idx] = { ...transfer };
            return tempDataLocal;
        });
    }

    const editableData = useMemo(
    () =>
      transferDataLocal
        ? TableService.getFieldsConfigForEditableTable(
          TABLE_CONFIG,
          transferDataLocal,
          {},
          handleDelete,
          props.readOnly || transferDataLocal?.length === 1,
          handleAddDocs,
          handleDeleteDocs
        )
        : [],
    [transferDataLocal, TABLE_CONFIG]
  );

    const updateRemainingTotalPriceAndTransferStatus = (transferData) => {
        let tempTransferData = [...transferData]
        let remainingPrice = tempTransferData[0].agrepay_total_price
        for (let index = tempTransferData.length - 1; index >= 0; index--) {
            let transfer = tempTransferData[index];
            remainingPrice = (parseFloat(remainingPrice).toFixed(2) - parseFloat(transfer.transfer_amount || 0).toFixed(2)).toFixed(2)
            transfer.remaining_total_price = remainingPrice

            let transferStatus = TRANSFER_STATUS_KIND_PARTIAL_PAYMENT
            if (transfer.transfer_amount == 0) {
                transferStatus = TRANSFER_STATUS_KIND_PENDING
            }
            else if (remainingPrice == 0) {
                transferStatus = TRANSFER_STATUS_KIND_PAID
            }
            else if (remainingPrice < 0) {
                transferStatus = TRANSFER_STATUS_KIND_EXCESS_PAYMENT
            }

            transfer.transfer_status_id = transferStatus.id
            transfer.transfer_status_kind = transferStatus.transfer_status_kind
            transfer.transfer_status_name = transferStatus.transfer_status_name
                
        }

        return tempTransferData
    }

    const onChangeTableDataLocal = useCallback(
        (name, value, idx) => {
        setTransferDataLocal((prev) => {
            let tempDataLocal = [...prev];
            let transfer = { ...tempDataLocal[idx] };
            if (name == 'transfer_date') {
                transfer[name] = DateService.getIsoDateFromDatetime(value) 
            }
            else {
                transfer[name] = value;
            }

            tempDataLocal[idx] = { ...transfer };
            
            if (name == 'transfer_amount') {
                tempDataLocal = updateRemainingTotalPriceAndTransferStatus(tempDataLocal)
            }

            return tempDataLocal;
        });
        },
        [setTransferDataLocal]
    );

    const isFormValid = () => (
        transferDataLocal && 
        transferDataLocal.length > 0 && 
        transferDataLocal.every(transfer=> transfer.transfer_date && transfer.transfer_amount && transfer.transfer_status_id)
    )

    const prepareDataToSend = (data) => {
        let dataToSend = []
        for (let index = data.length - 1; index >= 0; index--) {
            dataToSend.push({
                transfer_date: data[index].transfer_date,
                transfer_amount: data[index].transfer_amount,
                transfer_kind: data[index].transfer_kind_id,
                transfer_agreement: data[index].transfer_agreement_id,
                transfer_status: data[index].transfer_status_id,
                transfer_docs: data[index].transfer_docs,
                transfer_is_invoiced: data[index].transfer_is_invoiced,
            })
        }
        return dataToSend
        }

    const { onServerError } = useToasts();

    const { mutate: addTransfers, isPending: isAddingTransfers } = useMassCreateInvestmentTransferMutation({
        onSuccess: () => {
            toast.custom(
                () => <SnackbarSuccess message={t("successfullyEdited")} />,
                {
                    position: "bottom-right",
                },
            );
            props.onClose()
        },
        onError: (error) => {
            onServerError(error);
        },
    });

    const { mutate: returnTransfers, isPending: isReturningTransfers } = useRetrunInvestmentTransferForAgreementMutation({
        onSuccess: () => {
            toast.custom(
                () => <SnackbarSuccess message={t("successfullyEdited")} />,
                {
                    position: "bottom-right",
                },
            );
            props.onClose()
        },
        onError: (error) => {
            onServerError(error);
        },
    });

    

    const onSubmit = () => {
        addTransfers({id: props.agreementId, data:prepareDataToSend(transferDataLocal)})
    };

    const onReturnTransfers = () => {
        returnTransfers({id: props.agreementId})
    }


    return (
        <BasicDialog
            open={true}
            onClose={() => props.onClose()}
            titleAlign={"center"}
            contentAlign={"center"}
            title={props.isReturningTransfersDialog ? t("bankings.returnedTransfersDialogTitle") : t("bankings.addTransferAmount")}
            maxWidth={"xl"}
            showDialogActions
            alwaysFullScreen={false}
            contentSx={{ pt: 0 }}
        >
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={2}>
                    {!props.readOnly &&<Button fullWidth variant="contained" onClick={addEmptyTransfer}>
                            {t("bankings.add_transfer")}
                    </Button>}
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={!transferDataLocal}>
                        <EditableTable
                            data={editableData}
                            isLoading={false}
                            showTableLoader={false}
                            readOnly={props.readOnly}
                            editable={true}
                            showCheckbox={false}
                            showCleanFilterIcon={false}
                            onDataChange={onChangeTableDataLocal}
                            onBlur={() => { }}
                            style={{
                            maxHeight: "100vh",
                            }}
                            tableConfig={TABLE_CONFIG}
                            showContextMenu={false}
                            withParamsComparision={false}
                            showExportToFileButton={false}
                            enableExport={false}
                            summaryRow={<TransferSummaryRow transferData={transferDataLocal}/>}
                        />
                    </LoaderWrapper>
                     {!props.readOnly && <Grid item xs={12}>
                        <LoadingButton
                            loading={isAddingTransfers}
                            disabled={!isFormValid() || isAddingTransfers}
                            variant="contained"
                            color="primary"
                            sx={{ mt: 2 }}
                            onClick={onSubmit}
                            fullWidth
                        >
                            {t("save")}
                        </LoadingButton>
                    </Grid>}
                     {props.isReturningTransfersDialog && <Grid item container xs={12} justifyContent={"center"} columnSpacing={3}>
                        <Grid item xs={2}>
                            <LoadingButton
                                loading={isReturningTransfers}
                                disabled={isReturningTransfers}
                                variant="contained"
                                color="success"
                                sx={{ mt: 2 }}
                                onClick={onReturnTransfers}
                                fullWidth
                            >
                                {t("yes")}
                            </LoadingButton>
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                disabled={isReturningTransfers}
                                variant="contained"
                                color="error"
                                sx={{ mt: 2 }}
                                fullWidth
                                onClick={props.onClose}
                            >
                                {t("no")}
                            </Button>
                        </Grid>
                    </Grid>}
                </Grid>
            </Grid>
        </BasicDialog>
    );
}


function TransferSummaryRow(props){
    const { t } = useTranslation();
    return <StyledTableRow style={{backgroundColor: "#ededed"}}>
        <StyledTableCell colSpan={3}/> 
        <StyledTableCell colSpan={1} align="center">
            <Typography fontWeight={"bold"}>
                {t("agreements.AgreementProductsTable.total")}
            </Typography>
        </StyledTableCell>
        <StyledTableCell colSpan={1} align="center">
            <Typography fontWeight={"bold"}>
            {!props.transferData ? "-" : formatCurrency(props.transferData.reduce((currvalue, accumulator)=>{
                return parseFloat(currvalue) + parseFloat(accumulator.transfer_amount || 0)
            }, 0).toFixed(2), {showUnit : false})}
            </Typography>
        </StyledTableCell>
       <StyledTableCell colSpan={8}/>
    </StyledTableRow>
}